import React, { useState, useEffect, lazy, Suspense } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabase";
import { useProfileViews } from "../hooks/useProfileViews";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import { fetchSponsoredEmployerIds } from "../services/listingPurchases";

// Lazy load dashboard components for better performance
const TopJobMatches = lazy(() => import("../components/employee/dashboard/TopJobMatches"));
const JobApplicationTracker = lazy(() => import("../components/employee/dashboard/JobApplicationTracker"));
const DashboardStats = lazy(() => import("../components/employee/dashboard/DashboardStats"));
const DashboardAnalytics = lazy(() => import("../components/employee/dashboard/DashboardAnalytics"));
const CVAnalyserCard = lazy(() => import("../components/employee/dashboard/CVAnalyserCard"));

const EmployeeDashboard = () => {
    const { user } = useAuth();
    const { getProfileViewStats } = useProfileViews();
    const { showSuccess, showError } = useSnackbarNotification();
    const [selectedTab, setSelectedTab] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [allApplications, setAllApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalApplied: 0,
        totalInterviews: 0,
        totalOffers: 0,
        totalRejected: 0
    });
    const [internalStats, setInternalStats] = useState({
        totalInternalApplications: 0,
        totalInternalInterviews: 0,
        totalInternalOffers: 0,
        totalInternalRejected: 0
    });
    const [statsChanges, setStatsChanges] = useState({
        jobMatchesChange: 0,
        totalApplicationsChange: 0,
        totalInterviewsChange: 0,
        totalRejectedChange: 0
    });
    const [jobMatches, setJobMatches] = useState(0);
    const [topJobMatches, setTopJobMatches] = useState([]);
    const [bookmarkedInternalJobs, setBookmarkedInternalJobs] = useState([]);
    const [shareDropdown, setShareDropdown] = useState({ open: false, job: null });
    const [dismissedJobMatches, setDismissedJobMatches] = useState([]);
    const [profileAnalytics, setProfileAnalytics] = useState({
        totalViews: 0,
        totalClicks: 0,
        recentViews: 0,
        recentClicks: 0
    });
    const [jobMatchAnalytics, setJobMatchAnalytics] = useState({
        matchPercentage: 0,
        matchChange: 0
    });

    // Fetch all applications and job matches
    useEffect(() => {
        if (user) {
            fetchAllApplications();
            fetchProfileAnalytics();
            fetchBookmarkedInternalJobs();
            fetchDismissedJobMatches().then(() => {
                fetchJobMatches();
            });
        }
    }, [user]);

    // Fetch dismissed job matches from user metadata
    const fetchDismissedJobMatches = async () => {
        if (!user) return [];

        try {
            const dismissed = user.user_metadata?.dismissed_job_matches || [];
            setDismissedJobMatches(dismissed);
            return dismissed;
        } catch (error) {
            return [];
        }
    };

    // Fetch bookmarked internal jobs
    const fetchBookmarkedInternalJobs = async () => {
        if (!user) return;

        try {
            const { data, error } = await supabase
                .from('applications')
                .select('job_listing_id')
                .eq('user_id', user.id)
                .eq('status', 'bookmarked');

            if (error) throw error;

            setBookmarkedInternalJobs(data.map(item => item.job_listing_id));
        } catch (error) {
            // Error fetching bookmarked internal jobs
        }
    };

    const fetchAllApplications = async () => {
        try {
            setLoading(true);

            // Internal postings only (platform jobs) — not scraped external board rows
            const { data: internalData, error: internalError } = await supabase
                .from('applications')
                .select(`
                    *,
                    internal_job_listings!job_listing_id(
                        id,
                        title,
                        company_name,
                        location,
                        employment_type,
                        created_at
                    )
                `)
                .eq('user_id', user.id)
                .order('application_date', { ascending: false });

            if (internalError) throw internalError;

            const internalFormatted = (internalData || [])
                .filter(app => app.internal_job_listings)
                .map(app => ({
                    id: `internal-${app.id}`,
                    applicationId: app.id,
                    type: 'internal',
                    title: app.internal_job_listings?.title,
                    company: app.internal_job_listings?.company_name,
                    location: app.internal_job_listings?.location,
                    continent: null,
                    fulltime_parttime: app.internal_job_listings?.employment_type,
                    experience: null,
                    category: null,
                    deadline: null,
                    application_url: null,
                    created_at: app.internal_job_listings?.created_at,
                    application_date: app.application_date,
                    status: app.status || 'bookmarked',
                    admin_notes: null,
                }));

            setAllApplications(internalFormatted);
            calculateStats(internalFormatted);

            const statsLocal = {
                totalInternalApplications: internalFormatted.length,
                totalInternalInterviews: internalFormatted.filter(a => a.status === 'interview' || a.status === 'screening').length,
                totalInternalOffers: internalFormatted.filter(a => a.status === 'offer').length,
                totalInternalRejected: internalFormatted.filter(a => a.status === 'rejected').length
            };
            setInternalStats(statsLocal);

            calculateStatsChanges(internalFormatted);
        } catch (error) {
            // Error fetching applications
        } finally {
            setLoading(false);
        }
    };

    const fetchJobMatches = async () => {
        try {
            // Fetch employee languages from database, with fallback to user_metadata
            let employeeLanguages = null;
            try {
                const { data: profileData } = await supabase
                    .from('users_profiles')
                    .select('languages')
                    .eq('profileid', user.id)
                    .single();

                if (profileData?.languages) {
                    // Parse JSON string if needed
                    if (typeof profileData.languages === 'string') {
                        try {
                            employeeLanguages = JSON.parse(profileData.languages);
                        } catch {
                            employeeLanguages = null;
                        }
                    } else {
                        employeeLanguages = profileData.languages;
                    }
                }
            } catch (err) {
                // Database fetch failed, will try user_metadata below
            }

            // Fallback to user_metadata if database didn't have languages
            if (!employeeLanguages) {
                employeeLanguages = user.user_metadata?.my_profile?.profile_skills?.languages || null;
            }

            // Get employee profile data for matching
            const employeeProfile = {
                country: user.user_metadata?.country || user.user_metadata?.my_profile?.profile_personal_info?.country || null,
                provinceState: user.user_metadata?.province_state || user.user_metadata?.my_profile?.profile_personal_info?.provinceState || null,
                employmentType: user.user_metadata?.employment_status || null,
                trainingLevel: user.user_metadata?.training_level || null,
                // Use profile_role.categories (What category best fits what you're looking for? Select all that apply)
                category: user.user_metadata?.my_profile?.profile_role?.categories ||
                    user.user_metadata?.profile_categories ||
                    null,
                languages: employeeLanguages
            };


            // Get date ranges for percentage change calculation
            const now = new Date();
            const sevenDaysAgo = new Date(now);
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            const fourteenDaysAgo = new Date(now);
            fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

            let jobListings = [];

            // Fetch internal job listings
            if (user?.email.includes('@fourminutemedicine.com')) {
                try {
                    jobListings = await fetchAllIncludingDevModeJobs();
                } catch (error) {
                    console.error("Error fetching dev mode jobs:", error);
                    throw error;
                }
            } else {
                try {
                    jobListings = await fetchAllExcludingDevModeJobs();
                } catch (error) {
                    console.error("Error fetching non-dev mode jobs:", error);
                    throw error;
                }
            }

            // Fetch external job listings
            // const { data: externalJobListings, error: externalError } = await supabase
            //     .from('jobs')
            //     .select('*')
            //     .eq('live', true)
            //     .order('date_added', { ascending: false });

            // if (externalError) throw externalError;

            let sponsoredEmployerIds = new Set();
            try {
                const sponsorIds = await fetchSponsoredEmployerIds();
                sponsoredEmployerIds = new Set(sponsorIds);
            } catch (err) {
                // Error fetching sponsored employers
            }

            const thirtyDaysAgo = new Date(now);
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            // Get jobs from last 30 days
            let freshJobListings = (jobListings || []).filter(job => {
                if (!job?.created_at) return false;
                const createdAt = new Date(job.created_at);
                return createdAt >= thirtyDaysAgo;
            });

            // If no fresh jobs, use all available jobs (fallback)
            if (freshJobListings.length === 0) {
                freshJobListings = (jobListings || []).filter(job => {
                    if (!job?.created_at) return false;
                    const createdAt = new Date(job.created_at);
                    return createdAt >= thirtyDaysAgo;
                });
            }

            // If still no jobs, use all available jobs (fallback)
            if (freshJobListings.length === 0) {
                freshJobListings = jobListings || [];
            }

            // Calculate current week and last week job counts
            const currentWeekJobs = freshJobListings.filter(job =>
                new Date(job.created_at) >= sevenDaysAgo
            ).length;
            const lastWeekJobs = jobListings.filter(job => {
                const jobDate = new Date(job.created_at);
                return jobDate >= fourteenDaysAgo && jobDate < sevenDaysAgo;
            }).length;

            // Calculate percentage change for job matches
            const calculatePercentageChange = (current, last) => {
                if (last > 0) {
                    const change = Math.abs(Math.round(((current - last) / last) * 100));
                    return Math.min(change, 100); // Cap at 100%
                }
                // No previous data to compare - return 0 (no change to show)
                return 0;
            };

            const jobMatchesChange = calculatePercentageChange(currentWeekJobs, lastWeekJobs);
            setStatsChanges(prev => ({ ...prev, jobMatchesChange }));

            // freshJobListings = [...freshJobListings, ...externalJobListings];

            freshJobListings = [...freshJobListings];

            // Calculate matches and get top 3
            const matches = freshJobListings.map(job => {
                let score = 0;
                // Fixed-weight scoring (match `RecommendedMatches`): Location, Employment type,
                // Experience, Specialty = 25 points each (total 100).
                const totalChecks = 100;
                const matchedCategories = [];
                const matchDetails = {
                    jobTitle: job.title,
                    jobCompany: job.company_name,
                    location: {},
                    employmentType: {},
                    experience: {},
                    category: {},
                    languages: {}
                };

                // 1. Location match (25% weight) - Match RecommendedMatches calculation exactly
                if (employeeProfile.country && (job.country || job.location)) {
                    let locationScore = 0;
                    if (job.country) {
                        const employeeCountryLower = employeeProfile.country.toLowerCase().trim();
                        const jobCountryLower = job.country.toLowerCase().trim();
                        const countryMatch = employeeCountryLower === jobCountryLower ||
                            jobCountryLower.includes(employeeCountryLower) ||
                            employeeCountryLower.includes(jobCountryLower);

                        let provinceMatch = false;
                        if (employeeProfile.provinceState && job.province) {
                            const employeeProvinceLower = employeeProfile.provinceState.toLowerCase().trim();
                            const jobProvinceLower = job.province.toLowerCase().trim();
                            provinceMatch = employeeProvinceLower === jobProvinceLower ||
                                jobProvinceLower.includes(employeeProvinceLower) ||
                                employeeProvinceLower.includes(jobProvinceLower);
                        } else if (!employeeProfile.provinceState && !job.province) {
                            // If neither has province, give full province score
                            provinceMatch = true;
                        }

                        // Calculate location score: 12.5% for country, 12.5% for province (if applicable)
                        if (countryMatch) {
                            locationScore = 12.5; // Country match gives 12.5%
                            if (provinceMatch) {
                                locationScore = 25; // Both match gives full 25%
                            }
                        }
                    } else if (job.location) {
                        // Fallback to legacy location field matching - gives full 25%
                        const employeeCountryLower = employeeProfile.country.toLowerCase().trim();
                        const jobLocationLower = job.location.toLowerCase().trim();
                        const locationMatch = jobLocationLower.includes(employeeCountryLower) ||
                            employeeCountryLower.includes(jobLocationLower);
                        if (locationMatch) {
                            locationScore = 25;
                        }
                    }

                    if (locationScore > 0) {
                        score += Math.min(25, locationScore);
                        matchedCategories.push("Location");
                    }
                }

                // 1b province match (25% weight) for scraped jobs
                let provinceScore = 0;
                if (
                    employeeProfile.provinceState &&
                    job.province &&
                    !job.country &&
                    !employeeProfile.country
                ) {
                    const employeeProvinceLower = employeeProfile.provinceState.toLowerCase().trim();
                    const jobProvinceLower = job.province.toLowerCase().trim();
                    const provinceMatch = employeeProvinceLower === jobProvinceLower ||
                        jobProvinceLower.includes(employeeProvinceLower) ||
                        employeeProvinceLower.includes(jobProvinceLower);
                    if (provinceMatch) {
                        provinceScore = 12.5;
                    }
                    if (provinceScore > 0) score += Math.min(12.5, provinceScore);
                    if (provinceScore > 0) matchedCategories.push("Location");
                }

                // 2. Employment type match (25% weight) - Full time/Part time
                let employmentTypeScore = 0;
                if (job.employment_type && employeeProfile.employmentType) {
                    const jobType = job.employment_type.toLowerCase();
                    const employeeType = employeeProfile.employmentType.toLowerCase();
                    const employmentMatch = jobType === employeeType ||
                        (jobType.includes('full') && employeeType.includes('full')) ||
                        (jobType.includes('part') && employeeType.includes('part'));

                    if (employmentMatch) {
                        score += 25;
                        employmentTypeScore = 25;
                        matchedCategories.push("Employment type");
                    }

                    matchDetails.employmentType = {
                        employeeType: employeeProfile.employmentType,
                        jobType: job.employment_type,
                        match: employmentMatch,
                        score: employmentTypeScore
                    };
                }

                //2b employment type match (25% weight) for scraped jobs
                if (job.fulltime_parttime && employeeProfile.employmentType && !job.country) {
                    const jobType = job.fulltime_parttime.toLowerCase();
                    const employmentMatch = jobType.includes(employeeProfile.employmentType.toLowerCase());
                    if (employmentMatch) {
                        score += 25;
                        employmentTypeScore = 25;
                        matchedCategories.push("Employment type");
                    }
                }

                // 3. Experience level match (25% weight) - Employee training level vs Job experience level
                let experienceScore = 0;
                // Map employee training_level to job experience_level
                const mapTrainingToExperience = (trainingLevel) => {
                    if (!trainingLevel) return [];
                    const level = trainingLevel.toLowerCase();
                    // Map employee training levels to compatible job experience levels
                    switch (level) {
                        case 'student':
                            return ['medical-student'];
                        case 'junior':
                            return ['junior-doctor', 'medical-officer'];
                        case 'senior':
                            return ['registrar', 'fellow', 'specialist', 'consultant'];
                        case 'other':
                            // "other" can match any level
                            return ['medical-student', 'junior-doctor', 'medical-officer', 'registrar', 'fellow', 'specialist', 'consultant'];
                        default:
                            return [];
                    }
                };

                // Only check experience level if both have data
                let experienceMatch = false;
                if (job.experience_level && employeeProfile.trainingLevel) {
                    const jobExperienceLevel = job.experience_level.toLowerCase();
                    const compatibleLevels = mapTrainingToExperience(employeeProfile.trainingLevel);
                    experienceMatch = compatibleLevels.includes(jobExperienceLevel);
                    if (experienceMatch) {
                        score += 25;
                        experienceScore = 25;
                        matchedCategories.push("Experience");
                    }

                    matchDetails.experience = {
                        employeeTrainingLevel: employeeProfile.trainingLevel,
                        jobExperienceLevel: job.experience_level,
                        compatibleLevels,
                        match: experienceMatch,
                        score: experienceScore
                    };
                } else {
                    // If one or both don't have experience level, don't include in matching (don't penalize or reward)
                    matchDetails.experience = {
                        employeeTrainingLevel: employeeProfile.trainingLevel,
                        jobExperienceLevel: job.experience_level,
                        match: false,
                        score: 0,
                        note: 'Experience level not checked - one or both missing'
                    };
                }

                //3b experience level match (25% weight) for scraped jobs
                if (job.experience && employeeProfile.trainingLevel && !job.country) {
                    const jobExperienceLevel = job.experience.toLowerCase();
                    const experienceMatch = jobExperienceLevel.includes(employeeProfile.trainingLevel.toLowerCase());
                    if (experienceMatch) {
                        score += 25;
                        experienceScore = 25;
                        matchedCategories.push("Experience");
                    }
                }

                // 4. Category/Roles match (25% weight) - "What category best fits what you're looking for?" vs Job specialty
                let categoryScore = 0;
                if (job.specialty && employeeProfile.category) {
                    const jobCategory = job.specialty.toLowerCase();
                    // employeeProfile.category is an array from profile_role.categories
                    if (Array.isArray(employeeProfile.category)) {
                        // Check if any of the employee's selected categories matches the job specialty
                        const matchingCategory = employeeProfile.category.find(cat => {
                            if (!cat) return false;
                            const catStr = typeof cat === 'string' ? cat.toLowerCase() : String(cat).toLowerCase();
                            return catStr === jobCategory ||
                                catStr.includes(jobCategory) ||
                                jobCategory.includes(catStr);
                        });
                        const categoryMatch = !!matchingCategory;

                        if (categoryMatch) {
                            score += 25;
                            categoryScore = 25;
                            matchedCategories.push("Specialty");
                        }

                        matchDetails.category = {
                            employeeCategories: employeeProfile.category,
                            jobSpecialty: job.specialty,
                            matchingCategory: matchingCategory || null,
                            match: categoryMatch,
                            score: categoryScore
                        };
                    } else if (typeof employeeProfile.category === 'string') {
                        // Fallback for string format
                        const categoryMatch = employeeProfile.category.toLowerCase() === jobCategory ||
                            employeeProfile.category.toLowerCase().includes(jobCategory) ||
                            jobCategory.includes(employeeProfile.category.toLowerCase());

                        if (categoryMatch) {
                            score += 25;
                            categoryScore = 25;
                            matchedCategories.push("Specialty");
                        }

                        matchDetails.category = {
                            employeeCategory: employeeProfile.category,
                            jobSpecialty: job.specialty,
                            match: categoryMatch,
                            score: categoryScore
                        };
                    }
                }

                //4b category/roles match (25% weight) for scraped jobs
                if (job.category && employeeProfile.category && !job.country) {
                    const jobCategory = job.category.toLowerCase();
                    const categoryMatch = employeeProfile.category.includes(jobCategory);
                    if (categoryMatch) {
                        score += 25;
                        categoryScore = 25;
                        matchedCategories.push("Specialty");
                    }
                }

                // 5. Languages: captured for details only (does NOT affect match % here)
                // NOTE: RecommendedMatches does not include languages in scoring.
                let languageScore = 0;
                if (job.language_requirement && employeeProfile.languages) {

                    // Parse job language_requirement if it's a JSON string
                    let jobLanguages = job.language_requirement;
                    if (typeof jobLanguages === 'string') {
                        try {
                            jobLanguages = JSON.parse(jobLanguages);
                        } catch {
                            // If parsing fails, treat as single language string
                            jobLanguages = [jobLanguages];
                        }
                    }
                    // Ensure jobLanguages is an array
                    if (!Array.isArray(jobLanguages)) {
                        jobLanguages = [jobLanguages];
                    }

                    // Ensure employeeLanguages is an array
                    let employeeLangsArray = employeeProfile.languages;
                    if (!Array.isArray(employeeLangsArray)) {
                        employeeLangsArray = [employeeLangsArray];
                    }

                    // Normalize languages to lowercase strings for comparison
                    const normalizeLanguage = (lang) => {
                        if (!lang) return '';
                        const langStr = typeof lang === 'string' ? lang : String(lang);
                        return langStr.toLowerCase().trim();
                    };

                    const jobLangsNormalized = jobLanguages.map(normalizeLanguage).filter(l => l.length > 0);
                    const employeeLangsNormalized = employeeLangsArray.map(normalizeLanguage).filter(l => l.length > 0);

                    // Check if all required job languages are covered by employee languages
                    // Or if any job language matches any employee language (more lenient)
                    let languageMatch = false;
                    if (jobLangsNormalized.length > 0 && employeeLangsNormalized.length > 0) {
                        // Check if all job required languages are present in employee languages
                        const allRequiredMatch = jobLangsNormalized.every(jobLang =>
                            employeeLangsNormalized.some(empLang =>
                                empLang === jobLang ||
                                empLang.includes(jobLang) ||
                                jobLang.includes(empLang)
                            )
                        );

                        // Also check if any job language matches any employee language (more lenient approach)
                        const anyMatch = jobLangsNormalized.some(jobLang =>
                            employeeLangsNormalized.some(empLang =>
                                empLang === jobLang ||
                                empLang.includes(jobLang) ||
                                jobLang.includes(empLang)
                            )
                        );

                        // Use the more lenient match (at least one language matches)
                        languageMatch = anyMatch;

                        // If all required languages match, that's a full match
                        // For partial matches, we could give partial score, but for simplicity, 
                        // we'll give full 25% if any language matches
                        if (allRequiredMatch) {
                            languageMatch = true;
                        }
                    }

                    if (languageMatch) {
                        languageScore = 25;
                    }

                    matchDetails.languages = {
                        employeeLanguages: employeeLangsArray,
                        jobLanguages: jobLanguages,
                        languageMatch,
                        score: languageScore
                    };
                } else {
                    // If one or both don't have language data, don't include in matching
                    matchDetails.languages = {
                        employeeLanguages: employeeProfile.languages,
                        jobLanguages: job.language_requirement,
                        match: false,
                        score: 0,
                        note: 'Language data not checked - one or both missing'
                    };
                }

                // Calculate match percentage accurately
                // If totalChecks is 0 (no profile data), assign a default match percentage so jobs still show
                let matchPercentage = 0;
                if (
                    employeeProfile.country ||
                    employeeProfile.employmentType ||
                    employeeProfile.trainingLevel ||
                    employeeProfile.category
                ) {
                    const rawPercentage = (score / totalChecks) * 100;
                    matchPercentage = Math.round(rawPercentage);
                    // Ensure percentage is between 0 and 100
                    matchPercentage = Math.max(0, Math.min(100, matchPercentage));
                } else {
                    // If user has no profile data, assign a baseline match percentage
                    // This ensures jobs still show up even without profile matching
                    matchPercentage = 10; // Baseline 10% match
                }

                const postedDays = Math.max(
                    1,
                    Math.ceil((new Date() - new Date(job.created_at)) / (1000 * 60 * 60 * 24))
                );
                const isSponsored = sponsoredEmployerIds.has(job.employer_id);

                const uniqueMatchedCategories = [...new Set(matchedCategories)];

                return {
                    id: job.id,
                    matchPercentage,
                    matchedCategories: uniqueMatchedCategories,
                    title: job.title,
                    company: job.company_name,
                    location: job.location,
                    postedDays,
                    status: "available",
                    created_at: job.created_at,
                    isSponsored,
                    employer_id: job.employer_id,
                    application_url: job.application_url || null
                };
            })
                .sort((a, b) => {
                    if (a.isSponsored !== b.isSponsored) {
                        return a.isSponsored ? -1 : 1;
                    }
                    if (b.matchPercentage !== a.matchPercentage) {
                        return b.matchPercentage - a.matchPercentage;
                    }
                    return new Date(b.created_at) - new Date(a.created_at);
                });

            // Filter out dismissed job matches
            // Include ALL jobs regardless of match percentage (even 0% matches)
            const nonDismissedMatches = matches.filter(match =>
                match.matchPercentage > 0 && !dismissedJobMatches.includes(match.id)
            );

            // Show top 3 matches - sorted by sponsored status, then match percentage, then date
            // This ensures users see jobs even if they don't have perfect matches
            const topMatches = nonDismissedMatches.slice(0, 3);
            setTopJobMatches(topMatches);

            // Count only jobs the employee has applied to (internal applications with 'applied' status)
            // Fetch applications to count applied jobs
            try {
                const { data: appliedJobs, error: appsError } = await supabase
                    .from('applications')
                    .select('job_listing_id')
                    .eq('user_id', user.id)
                    .eq('status', 'applied');

                if (!appsError && appliedJobs) {
                    // Count unique job listings the user has applied to
                    const uniqueAppliedJobIds = new Set(appliedJobs.map(app => app.job_listing_id).filter(Boolean));
                    setJobMatches(uniqueAppliedJobIds.size);
                } else {
                    // Fallback to 0 if error fetching applications
                    setJobMatches(0);
                }
            } catch (err) {
                setJobMatches(0);
            }


            // Calculate average job match percentage for analytics
            if (matches.length > 0) {
                // Calculate average accurately: sum all percentages, divide by count
                const totalMatchSum = matches.reduce((sum, job) => sum + job.matchPercentage, 0);
                const avgMatchPercentage = Math.round(totalMatchSum / matches.length);

                // Calculate match change (compare current week vs last week average)
                const currentWeekMatches = matches.filter(job => {
                    const jobDate = new Date(job.created_at);
                    return jobDate >= sevenDaysAgo;
                });
                const lastWeekMatches = matches.filter(job => {
                    const jobDate = new Date(job.created_at);
                    return jobDate >= fourteenDaysAgo && jobDate < sevenDaysAgo;
                });

                // Calculate current week average accurately
                let currentWeekAvg = 0;
                if (currentWeekMatches.length > 0) {
                    const totalSum = currentWeekMatches.reduce((sum, job) => sum + job.matchPercentage, 0);
                    const rawAvg = totalSum / currentWeekMatches.length;
                    currentWeekAvg = Math.round(rawAvg);
                    currentWeekAvg = Math.max(0, Math.min(100, currentWeekAvg));
                }

                // Calculate last week average accurately
                let lastWeekAvg = 0;
                if (lastWeekMatches.length > 0) {
                    const totalSum = lastWeekMatches.reduce((sum, job) => sum + job.matchPercentage, 0);
                    const rawAvg = totalSum / lastWeekMatches.length;
                    lastWeekAvg = Math.round(rawAvg);
                    lastWeekAvg = Math.max(0, Math.min(100, lastWeekAvg));
                }

                // Calculate percentage change accurately
                let matchChange = 0;
                if (lastWeekAvg > 0) {
                    // Calculate actual percentage change: ((current - last) / last) * 100
                    const change = ((currentWeekAvg - lastWeekAvg) / lastWeekAvg) * 100;
                    matchChange = Math.round(Math.abs(change));
                    // Cap at 100%
                    matchChange = Math.min(matchChange, 100);
                } else if (currentWeekAvg > 0) {
                    // Going from 0% to a positive percentage
                    matchChange = Math.min(currentWeekAvg, 100);
                }

                setJobMatchAnalytics({
                    matchPercentage: avgMatchPercentage,
                    matchChange
                });
            } else {
                setJobMatchAnalytics({ matchPercentage: 0, matchChange: 0 });
            }
        } catch (error) {
            // Error fetching job matches
        }
    };

    const fetchAllIncludingDevModeJobs = async () => {
        const { data: jobs, error: jobsError } = await supabase
            .from('internal_job_listings')
            .select('*')
            .eq('id', '47b6b31b-c946-45d2-9485-03782e5cea13')
            .order('created_at', { ascending: false });
        return jobs;
    };

    const fetchAllExcludingDevModeJobs = async () => {  
        const { data: jobs, error: jobsError } = await supabase
            .from('internal_job_listings')
            .select('*')
            .eq('is_dev_job', false)
            .eq('id', '47b6b31b-c946-45d2-9485-03782e5cea13')
            .order('created_at', { ascending: false });
        return jobs;
    };

    const handleShareClick = (job, event) => {
        event.stopPropagation();
        setShareDropdown({ open: true, job });
    };

    const handleCloseShare = () => {
        setShareDropdown({ open: false, job: null });
    };

    const handleRemoveJobMatch = async (jobId) => {
        const confirmed = window.confirm("Are you sure you want to remove this job match?");
        if (confirmed) {
            try {
                // Update local state immediately
                setTopJobMatches(prev => prev.filter(job => job.id !== jobId));
                const updatedDismissed = [...dismissedJobMatches, jobId];
                setDismissedJobMatches(updatedDismissed);

                // Save to user metadata
                const { error } = await supabase.auth.updateUser({
                    data: {
                        dismissed_job_matches: updatedDismissed
                    }
                });

                if (error) throw error;

                showSuccess("Job match removed");
            } catch (error) {
                // Revert local state on error
                setDismissedJobMatches(dismissedJobMatches);
                fetchJobMatches(); // Refetch to restore
                showError("Failed to remove job match. Please try again.");
            }
        }
    };

    const fetchProfileAnalytics = async () => {
        // Temporarily disabled - set to 0 until fixed
        setProfileAnalytics({
            totalViews: 0,
            totalClicks: 0,
            recentViews: 0,
            recentClicks: 0
        });

        // Original code commented out until profile views are fixed
        /*
        if (!user?.id) {
            setProfileAnalytics({
                totalViews: 0,
                totalClicks: 0,
                recentViews: 0,
                recentClicks: 0
            });
            return;
        }
        
        try {
            const stats = await getProfileViewStats(user.id);
            
            // Always update state, even if stats is null
            if (stats) {
                // Calculate recent views (past 7 days)
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                
                const recentViews = stats.recentViews?.filter(view => 
                    new Date(view.view_timestamp) >= sevenDaysAgo
                ).length || 0;

                setProfileAnalytics({
                    totalViews: stats.totalViews || 0,
                    totalClicks: stats.totalViews || 0, // Same as profile views
                    recentViews: recentViews,
                    recentClicks: recentViews // Same as recent views
                });
            } else {
                // If stats is null, reset to 0
                setProfileAnalytics({
                    totalViews: 0,
                    totalClicks: 0,
                    recentViews: 0,
                    recentClicks: 0
                });
            }
        } catch (error) {
            console.error('Error fetching profile analytics:', error);
            // Reset to 0 on error
            setProfileAnalytics({
                totalViews: 0,
                totalClicks: 0,
                recentViews: 0,
                recentClicks: 0
            });
        }
        */
    };

    const calculateStats = (applications) => {
        const stats = {
            totalApplied: applications.filter(app => app.status === 'applied').length,
            totalInterviews: applications.filter(app => app.status === 'interview').length,
            totalOffers: applications.filter(app => app.status === 'offer').length,
            totalRejected: applications.filter(app => app.status === 'rejected').length
        };
        setStats(stats);
    };

    const calculateStatsChanges = (applications) => {
        try {
            const now = new Date();
            const sevenDaysAgo = new Date(now);
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            const fourteenDaysAgo = new Date(now);
            fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

            // Helper function to calculate percentage change
            const calculatePercentageChange = (current, last) => {
                if (last > 0) {
                    const change = Math.abs(Math.round(((current - last) / last) * 100));
                    return Math.min(change, 100); // Cap at 100%
                }
                // No previous data to compare - return 0 (no change to show)
                return 0;
            };

            const apps = applications || [];
            const currentWeekApps = apps.filter(app => {
                const appDate = new Date(app.application_date);
                return appDate >= sevenDaysAgo;
            });
            const lastWeekApps = apps.filter(app => {
                const appDate = new Date(app.application_date);
                return appDate >= fourteenDaysAgo && appDate < sevenDaysAgo;
            });

            const currentWeekApplied = currentWeekApps.filter(a => a.status === 'applied').length;
            const lastWeekApplied = lastWeekApps.filter(a => a.status === 'applied').length;
            const totalApplicationsChange = calculatePercentageChange(
                currentWeekApplied,
                lastWeekApplied
            );

            const currentWeekInterviews = currentWeekApps.filter(a => a.status === 'interview').length;
            const lastWeekInterviews = lastWeekApps.filter(a => a.status === 'interview').length;
            const totalInterviewsChange = calculatePercentageChange(
                currentWeekInterviews,
                lastWeekInterviews
            );

            const currentWeekRejected = currentWeekApps.filter(a => a.status === 'rejected').length;
            const lastWeekRejected = lastWeekApps.filter(a => a.status === 'rejected').length;
            const totalRejectedChange = calculatePercentageChange(
                currentWeekRejected,
                lastWeekRejected
            );

            setStatsChanges(prev => ({
                ...prev,
                totalApplicationsChange,
                totalInterviewsChange,
                totalRejectedChange
            }));
        } catch (error) {
            // Error calculating stats changes
        }
    };

    // Handle bookmarking internal jobs
    const handleBookmarkInternalJob = async (jobId) => {
        if (!user) return;

        try {
            const isBookmarked = bookmarkedInternalJobs.includes(jobId);

            if (isBookmarked) {
                // Remove bookmark - delete the application record
                const { error } = await supabase
                    .from('applications')
                    .delete()
                    .eq('user_id', user.id)
                    .eq('job_listing_id', jobId)
                    .eq('status', 'bookmarked');

                if (error) throw error;

                setBookmarkedInternalJobs(prev => prev.filter(id => id !== jobId));
                showSuccess("Job removed from bookmarks");
            } else {
                // Add bookmark - create application with bookmarked status
                const now = new Date().toISOString();
                const { error } = await supabase
                    .from('applications')
                    .upsert({
                        user_id: user.id,
                        job_listing_id: jobId,
                        status: 'bookmarked',
                        stage: 'Applications',
                        application_date: now,
                        last_updated: now
                    }, { onConflict: 'user_id,job_listing_id' });

                if (error) throw error;

                setBookmarkedInternalJobs(prev => [...prev, jobId]);
                showSuccess("Job bookmarked successfully");
                // Refresh applications to show the new bookmark
                fetchAllApplications();
            }
        } catch (error) {
            showError("Failed to update bookmark. Please try again.");
        }
    };

    const handleStatusChange = async (applicationId, newStatus) => {
        try {
            const application = allApplications.find((app) => app.id === applicationId);
            if (!application) return;

            // Update local state immediately
            setAllApplications((prevApps) =>
                prevApps.map((app) =>
                    app.id === applicationId ? { ...app, status: newStatus } : app
                )
            );

            const { error } = await supabase
                .from('applications')
                .update({
                    status: newStatus,
                    last_updated: new Date().toISOString()
                })
                .eq('id', application.applicationId);

            if (error) throw error;

            // Recalculate stats
            const updatedApplications = allApplications.map((app) =>
                app.id === applicationId ? { ...app, status: newStatus } : app
            );
            calculateStats(updatedApplications);
        } catch (error) {
            fetchAllApplications(); // Refresh data on error
        }
    };


    const formatApplicationDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) return "1 day ago";
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
        return `${Math.ceil(diffDays / 30)} months ago`;
    };


    // Calculate tab counts dynamically
    const getTabCount = (tab) => {
        if (tab === "All") return allApplications.length;
        if (tab === "Applied") return allApplications.filter(app => app.status === "applied").length;
        if (tab === "Offer") return allApplications.filter(app => app.status === "offer").length;
        if (tab === "Non-responses") return allApplications.filter(app => app.status === "no responses").length;
        if (tab === "Rejected") return allApplications.filter(app => app.status === "rejected").length;
        return 0;
    };

    // Filter applications based on tab and search query
    const filteredApplications = allApplications.filter(app => {
        // Tab filtering
        let matchesTab = true;
        if (selectedTab === "All") matchesTab = true;
        else if (selectedTab === "Applied") matchesTab = app.status === "applied";
        else if (selectedTab === "Offer") matchesTab = app.status === "offer";
        else if (selectedTab === "Non-responses") matchesTab = app.status === "no responses";
        else if (selectedTab === "Rejected") matchesTab = app.status === "rejected";

        // Search filtering
        let matchesSearch = true;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            matchesSearch =
                app.title?.toLowerCase().includes(query) ||
                app.company?.toLowerCase().includes(query) ||
                app.location?.toLowerCase().includes(query);
        }

        return matchesTab && matchesSearch;
    }).slice(0, 3); // Limit to 3 for dashboard preview


    return (
        <>
            <style>{`
            @media (max-width: 1024px) {
                .employee-dashboard-container {
                    padding: 1rem !important;
                }
                .employee-dashboard-container h2 {
                    font-size: 1.125rem !important;
                }
                .employee-dashboard-grid {
                    grid-template-columns: 1fr !important;
                    gap: 1rem !important;
                }
                .employee-dashboard-card {
                    padding: 1rem !important;
                }
                .employee-dashboard-card h2 {
                    font-size: 1rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .employee-dashboard-stats-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
                .employee-dashboard-stat-card {
                    padding: 0.75rem !important;
                }
                .employee-dashboard-stat-card h3 {
                    font-size: 0.875rem !important;
                }
                .employee-dashboard-stat-card .text-2xl {
                    font-size: 1.5rem !important;
                }
                .employee-dashboard-job-match-card {
                    padding: 0.75rem !important;
                }
                .employee-dashboard-job-match-card h3 {
                    font-size: 0.875rem !important;
                    line-height: 1.25rem !important;
                }
                .employee-dashboard-job-match-card button {
                    font-size: 0.75rem !important;
                    padding: 0.5rem 0.75rem !important;
                }
                .employee-dashboard-tabs {
                    flex-wrap: wrap !important;
                    gap: 0.25rem !important;
                }
                .employee-dashboard-tabs button {
                    font-size: 0.625rem !important;
                    padding: 0.375rem 0.5rem !important;
                }
                .employee-dashboard-search {
                    width: 100% !important;
                    margin-top: 0.5rem !important;
                }
                .employee-dashboard-search input {
                    font-size: 0.75rem !important;
                    padding: 0.5rem 0.5rem 0.5rem 1.75rem !important;
                }
                .employee-dashboard-analytics {
                    padding: 0.75rem !important;
                }
                .employee-dashboard-analytics h3 {
                    font-size: 0.875rem !important;
                }
                .employee-dashboard-cv-card {
                    padding: 1rem !important;
                }
                .employee-dashboard-cv-card h3 {
                    font-size: 0.875rem !important;
                }
                .employee-dashboard-cv-card p {
                    font-size: 0.75rem !important;
                }
                .employee-dashboard-cv-card button {
                    font-size: 0.75rem !important;
                    padding: 0.5rem 0.75rem !important;
                }
            }
        `}</style>
            <div className="max-w-7xl mx-auto p-6 bg-white min-h-screen employee-dashboard-container">
                {/* Top Row - Top Job Matches and Job Application Tracker */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6 employee-dashboard-grid">
                    <Suspense fallback={<div className="flex justify-center items-center py-8"><div className="text-gray-500">Loading...</div></div>}>
                        <TopJobMatches
                            topJobMatches={topJobMatches}
                            bookmarkedInternalJobs={bookmarkedInternalJobs}
                            shareDropdown={shareDropdown}
                            handleShareClick={handleShareClick}
                            handleCloseShare={handleCloseShare}
                            handleBookmarkInternalJob={handleBookmarkInternalJob}
                            handleRemoveJobMatch={handleRemoveJobMatch}
                        />
                        <JobApplicationTracker
                            selectedTab={selectedTab}
                            setSelectedTab={setSelectedTab}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            filteredApplications={filteredApplications}
                            loading={loading}
                            handleStatusChange={handleStatusChange}
                            getTabCount={getTabCount}
                        />
                    </Suspense>
                </div>

                {/* Bottom Section - Left Half (2x2 Grid) and Right Half (2 Cards) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 employee-dashboard-grid">
                    {/* Left Half - Statistics Cards 2x2 Grid */}
                    <Suspense fallback={<div className="flex justify-center items-center py-8"><div className="text-gray-500">Loading...</div></div>}>
                        <DashboardStats
                            jobMatches={jobMatches}
                            stats={stats}
                            statsChanges={statsChanges}
                        />
                    </Suspense>

                    {/* Right Half - Analytics and CV Analyser */}
                    {/* className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 lg:mt-0" */}
                    <div className="grid grid-cols-1 gap-4 mt-6 lg:mt-0">
                        <Suspense fallback={<div className="flex justify-center items-center py-8"><div className="text-gray-500">Loading...</div></div>}>
                            {/* <DashboardAnalytics
                            jobMatchAnalytics={jobMatchAnalytics}
                            profileAnalytics={profileAnalytics}
                        /> */}
                            <CVAnalyserCard />
                        </Suspense>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EmployeeDashboard;